<template>
  <div
    ref="dropdownRef"
    class="dropdown-container"
    :class="{ 'dropdown-container__disabled': disabled }"
    @keyup="onNextOption"
  >
    <div
      v-if="label"
      class="dropdown-container__label"
      :class="{ 'dropdown-container__label__disabled': disabled }"
    >
      {{ label }} <span v-if="required" class="required"> *</span>
    </div>
    <DropdownHeader
      class="dropdown-container__header"
      :hasSearch="hasSearch"
      :isOpen="isOpen"
      :searchType="searchType"
      :title="title"
      :value="inputText"
      :disabled="disabled"
      @toggle="toggleDropdown"
      @change="onTextChange"
      @focus="isOpen = true"
      @keydown.tab="isOpen = false"
      @keyup.tab="isOpen = true"
    />
    <div v-show="isOpen" class="dropdown-container__window" @mouseleave="showTooltip = false">
      <!-- class options__no_scroll might need to check for other length if window height changes! -->
      <div class="options" :class="{ options__no_scroll: filteredOptions.length < 5 }">
        <div v-if="multiSelect">
          <CheckBoxStandard
            class="checkbox"
            :title="$t('general__data__select-all')"
            :checked="isAllSelected"
            @valueChanged="selectAll"
          />
          <DividerHorizontal margin="medium" />
        </div>
        <DropdownRow
          v-for="(option, index) in filteredOptions"
          :key="option.id"
          :activeIndex="activeIndex"
          :disabled="option.disabled"
          :index="index"
          :lastNavigationWay="lastNavigationWay"
          :selected="option.selected"
          :subtitle="option.subtitle ? option.subtitle : ''"
          :title="option.name"
          @onClicked="onSelect(option.id, option.disabled)"
          @onHover="(e) => trackPointer(e, index, option.name, option.subtitle ?? '')"
        />
      </div>
    </div>
    <TooltipStandard
      v-show="showTooltip"
      arrowPosition="top-center"
      class="tooltip"
      :pointerEvent="trackedMouseEvent"
      :text="hoveredTitle"
    />
  </div>
</template>
<script lang="ts" setup>
import { computed, onMounted, onUnmounted, ref, toRefs, watch } from 'vue'
import CheckBoxStandard from '@/design-system/atoms/CheckBoxStandard.vue'
import DropdownRow from '@/design-system/atoms/DropdownRow.vue'
import DropdownHeader from '@/design-system/atoms/DropdownHeader.vue'
import TooltipStandard from '@/design-system/atoms/TooltipStandard.vue'
import DividerHorizontal from '@/design-system/atoms/DividerHorizontal.vue'

import { useI18n } from 'vue-i18n'
export type optionType = {
  disabled: boolean
  id: string
  name: string
  selected: boolean
  subtitle?: string
  value?: Array<any>
}
interface MAP_MENU_PROPS {
  disabled?: boolean
  hasSearch?: boolean
  isAllSelected?: boolean
  label?: string
  multiSelect?: boolean
  options: optionType[]
  required?: boolean
  searchType?: string
  title: string
}
interface EMITS {
  (e: 'select', id: string): void
  (e: 'close'): void
  (e: 'selectAll')
}
const props = defineProps<MAP_MENU_PROPS>()
const emits = defineEmits<EMITS>()
const { title, options, searchType } = toRefs(props)
const { t } = useI18n()

const activeIndex = ref(0)
const dropdownRef = ref<HTMLElement | null>(null)
const hoveredTitle = ref<string>('')
const inputText = ref<string>('')
const isOpen = ref(false)
const lastNavigationWay = ref<'hover' | 'key' | ''>('')
const showTooltip = ref(false)
const trackedMouseEvent = ref<object>()

let lastMousePosition = [0, 0]

onMounted(() => document.addEventListener('click', close))
onUnmounted(() => document.removeEventListener('click', close))

const filteredOptions = computed(() => {
  if (['', t('general__text__all')].includes(inputText.value)) return options.value
  return options.value.filter((option) =>
    option.name.toLowerCase().includes(inputText.value.toLowerCase())
  )
})

function close(event: MouseEvent | TouchEvent) {
  if (!isOpen.value) return
  inputText.value = ''
  const target = event.target as HTMLElement
  if (!dropdownRef.value?.contains(target)) {
    isOpen.value = false
    activeIndex.value = 0
    emits('close')
  }
}
function onNextOption(event: KeyboardEvent) {
  if (!isOpen.value) return
  showTooltip.value = false
  if (event.key === 'ArrowDown' && activeIndex.value < filteredOptions.value.length - 1) {
    activeIndex.value++
    lastNavigationWay.value = 'key'
  } else if (event.key === 'ArrowUp' && activeIndex.value > 0) {
    activeIndex.value--
    lastNavigationWay.value = 'key'
  } else if (event.key === 'Enter') {
    const option = filteredOptions.value[activeIndex.value]
    if (option) onSelect(option.id, option.disabled)
  }
}
function onSelect(id: string, disabled: boolean) {
  if (disabled) return
  options.value.filter((opt) => {
    if (opt.id === id) {
      opt.selected = true
      inputText.value = opt.name
    } else {
      opt.selected = false
      inputText.value = ''
    }
  })
  if (!props.multiSelect) isOpen.value = false
  emits('select', id)
}
function onTextChange(text: string) {
  isOpen.value = true
  inputText.value = text
  activeIndex.value = 0
}
function toggleDropdown() {
  if (props.disabled) return
  isOpen.value = !isOpen.value
  if (!isOpen.value) {
    emits('close')
  }
  inputText.value = ''
  activeIndex.value = 0
}
function trackPointer(event, index: number, tooltipText: string, subtitle: string | undefined) {
  hoveredTitle.value = subtitle ? tooltipText + ' (' + subtitle + ')' : tooltipText
  const rect = event.currentTarget.getBoundingClientRect()
  if (event) {
    if (lastMousePosition[0] === event.clientX && lastMousePosition[1] === event.clientY) return
    lastMousePosition = [event.clientX, event.clientY]
    trackedMouseEvent.value = {
      x: rect.left + rect.width / 2,
      y: rect.bottom + rect.height / 2
    }
    const targetEl = event.target as HTMLElement
    if (targetEl.firstElementChild) {
      showTooltip.value =
        targetEl.firstElementChild.clientWidth < targetEl.firstElementChild.scrollWidth
    }
  }
  activeIndex.value = index
  lastNavigationWay.value = 'hover'
}

function selectAll() {
  emits('selectAll')
}
watch(title, (newTitle) => {
  if (newTitle) inputText.value = ''
})
</script>
<style lang="scss" scoped>
@use '@/design-system/design-tokens/variables.scss';
@use '@/design-system/design-tokens/font-mixins.scss';
@use '@/design-system/design-tokens/mixins.scss';

.dropdown-container {
  position: relative;
  &__disabled {
    cursor: not-allowed;
  }
  &__header {
    z-index: 1;
  }
  &__label {
    @include font-mixins.desktop-bold-paragraph;
    color: variables.$color-greyscale-content;
    display: flex;
    flex-direction: row;
    margin-bottom: 8px;
    width: fit-content;
    &__disabled {
      color: variables.$color-greyscale-secondary;
    }
    & .required {
      color: variables.$color-error;
      margin-left: 4px;
    }
  }
  &__window {
    align-items: flex-start;
    background: variables.$color-pure-white;
    border-radius: variables.$border-radius-medium;
    border: variables.$border-width-xsmall solid variables.$color-greyscale-secondary;
    box-shadow: variables.$box-shadow-elevation-3;
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    float: left;
    max-height: variables.$dropdown-window-height;
    margin-top: variables.$margin-medium;
    overflow: hidden;
    padding: variables.$padding-medium 0;
    position: absolute;
    width: 100%;
    z-index: 2;
    & .options {
      margin-right: variables.$margin-medium;
      overflow-x: hidden;
      overflow-y: auto;
      width: calc(#{100%} - #{variables.$margin-medium});
      &__no_scroll {
        width: 100%;
      }
      @include mixins.scrollbar-vertical;
    }
  }
  .tooltip {
    margin-top: -(variables.$margin-medium);
    width: max-content;
    z-index: 3;
  }
}

.checkbox {
  margin: variables.$margin-small variables.$margin-xlarge;
}
</style>
